import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TypoCaption } from "@ui/text/Typo";
import { ITheme } from "@ui/theme/GlobalTheme";

/** Dates travel as local calendar days in "YYYY-MM-DD"; an empty string means "not set". */
export const toISODate = (date: Date) =>
	`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export const fromISODate = (value: string): Date | null => {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
	if (!match) return null;
	const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
	return toISODate(date) === value ? date : null;
};

/** For callers that store a UTC-midnight Date (new Date("YYYY-MM-DD")) and send it with toISOString(); "" when invalid. */
export const toUTCISODate = (date: Date | null | undefined) => date && !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : "";

const addDays = (value: string, days: number) => {
	const date = fromISODate(value)!;
	date.setDate(date.getDate() + days);
	return toISODate(date);
};
const monthOf = (value: string) => `${value.slice(0, 7)}-01`;
const addMonths = (month: string, months: number) => {
	const date = fromISODate(month)!;
	return toISODate(new Date(date.getFullYear(), date.getMonth() + months, 1));
};
// Keeps the day of month where possible (31 Jan + 1 month → 28/29 Feb).
const shiftMonths = (value: string, months: number) => {
	const date = fromISODate(value)!;
	const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
	const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
	target.setDate(Math.min(date.getDate(), lastDay));
	return toISODate(target);
};
const weekStart = (value: string) => addDays(value, -((fromISODate(value)!.getDay() + 6) % 7));
const dayCount = (from: string, to: string) => Math.round((fromISODate(to)!.getTime() - fromISODate(from)!.getTime()) / 86_400_000) + 1;

export interface DateRangePreset {
	label: string;
	/** The range the preset applies; also marks it active when it matches the current value. */
	range?: [string, string];
	/** For parents that own preset state themselves (e.g. index-based periods); called instead of onChange. */
	onSelect?: () => void;
	active?: boolean;
}

type TFn = (key: string, options?: Record<string, unknown>) => string;

interface DatePickerFieldProps {
	/** Keep the calendar inside a native modal dialog when supplied. */
	portalContainer?: () => HTMLElement;
	timeZone?: "UTC";
	value: string;
	onChange: (value: string) => void;
	label?: string;
	min?: string;
	max?: string;
	allowClear?: boolean;
	placeholder?: string;
	disabled?: boolean;
	size?: "small" | "medium";
	invalid?: boolean;
	id?: string;
	autoOpen?: boolean;
	onDismiss?: () => void;
	style?: CSSProperties;
}

/** Single-date counterpart of DateRangePicker: same field, calendar and keyboard, value in "YYYY-MM-DD". */
export const DatePickerField = ({ value, onChange, ...props }: DatePickerFieldProps) =>
	{ useLocale(); return (<DateRangePicker {...props} single from={value} to={value} onChange={next => onChange(next)} />); };

export const getDefaultDateRangePresets = (t: TFn, today = toISODate(new Date())): DateRangePreset[] => {
	const month = monthOf(today);
	return [
		{ label: t("dashboard.periods.today"), range: [today, today] },
		{ label: t("dashboard.periods.yesterday"), range: [addDays(today, -1), addDays(today, -1)] },
		{ label: t("dashboard.periods.last7Days"), range: [addDays(today, -6), today] },
		{ label: t("dashboard.periods.last30Days"), range: [addDays(today, -29), today] },
		{ label: t("dashboard.periods.thisMonth"), range: [month, today] },
		{ label: t("dashboard.periods.lastMonth"), range: [addMonths(month, -1), addDays(month, -1)] },
	];
};

const Field = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 3px;
	min-width: 0;
	font-family: "Euclid Circular B", sans-serif;
	.trigger {
		display: flex; align-items: center; gap: 8px; width: 100%; height: 36px; box-sizing: border-box; padding: 0 12px;
		border: 1px solid ${({ theme }) => (theme as ITheme).colors.inputBorderPrimary}; border-radius: 4px; background: ${({ theme }) => (theme as ITheme).colors.panelBackgroundPrimary}; color: ${({ theme }) => (theme as ITheme).colors.textPrimary}; font: inherit; font-size: 13px; line-height: 1.25; text-align: left; cursor: pointer;
		box-shadow: none; transition: border-color .2s;
	}
	.trigger:hover:not(:disabled) { border-color: #35539f; }
	.trigger:focus-visible, .trigger[aria-expanded="true"] { outline: none; border-color: #35539f; box-shadow: none; }
	.trigger:disabled { background: #f9fafb; color: #98a2b3; cursor: default; }
	.trigger > svg { flex-shrink: 0; color: #667085; }
	.value { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-variant-numeric: tabular-nums; }
	.value.placeholder { color: #98a2b3; }
	.timezone { flex-shrink: 0; color: #667085; font-size: 11px; }
	.preset { flex-shrink: 0; padding: 1px 7px; border: 1px solid #d0d5dd; border-radius: 6px; background: #f9fafb; color: #344054; font-size: 11px; font-weight: 500; white-space: nowrap; }
	.control { position: relative; min-width: 0; }
	&.medium .trigger { height: 46px; padding: 0 16px; font-size: 1rem; }
	.trigger.invalid { border-color: #f04438; }
	.trigger.invalid:focus-visible, .trigger.invalid[aria-expanded="true"] { box-shadow: 0 0 0 3px rgba(240, 68, 56, .14); }
	.trigger.clearable { padding-right: 56px; }
	.trigger.clearable > .chevron { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); }
	.clear {
		position: absolute; right: 30px; top: 50%; transform: translateY(-50%); display: grid; place-items: center; width: 20px; height: 20px; padding: 0;
		border: 0; border-radius: 5px; background: transparent; color: #98a2b3; cursor: pointer;
	}
	.clear:hover { background: #f2f4f7; color: #344054; }
	.clear:focus-visible { outline: 2px solid #141413; outline-offset: 1px; }
`;

const Popover = styled.div`
	position: fixed;
	z-index: 1100;
	display: flex;
	max-width: calc(100vw - 16px);
	max-height: calc(100dvh - 16px);
	overflow: auto;
	box-sizing: border-box;
	border: 1px solid #d0d5dd;
	border-radius: 12px;
	background: #fff;
	box-shadow: 0 16px 40px rgba(16, 24, 40, .16), 0 2px 6px rgba(16, 24, 40, .06);
	color: #344054;
	font: 13px/1.5 "Euclid Circular B", sans-serif;
	&.narrow { flex-direction: column; }
	button { font: inherit; }
	.presets { display: flex; flex-direction: column; gap: 2px; flex-shrink: 0; width: 168px; box-sizing: border-box; padding: 12px; border-right: 1px solid #eaecf0; background: #f9fafb; border-radius: 12px 0 0 12px; }
	&.narrow .presets { flex-direction: row; width: auto; overflow-x: auto; border-right: 0; border-bottom: 1px solid #eaecf0; border-radius: 12px 12px 0 0; }
	.presets-title { margin: 2px 8px 6px; color: #667085; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
	&.narrow .presets-title { display: none; }
	.preset { padding: 7px 10px; border: 0; border-radius: 8px; background: transparent; color: #344054; text-align: left; white-space: nowrap; cursor: pointer; }
	.preset:hover { background: #f2f4f7; }
	.preset[aria-pressed="true"] { background: #fff; box-shadow: inset 0 0 0 1px #d0d5dd; color: #182230; font-weight: 600; }
	.preset:focus-visible { outline: 2px solid #141413; outline-offset: -2px; }
	.main { display: flex; flex-direction: column; min-width: 0; padding: 14px 16px 12px; }
	.inputs { display: flex; align-items: flex-end; gap: 8px; }
	.inputs label { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 0; color: #667085; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
	.inputs input {
		width: 100%; height: 34px; box-sizing: border-box; padding: 0 10px; border: 1px solid #d0d5dd; border-radius: 8px;
		color: #182230; font: inherit; font-size: 13px; letter-spacing: 0; text-transform: none; font-variant-numeric: tabular-nums;
	}
	.inputs input:focus, .inputs input.pending { outline: none; border-color: #141413; box-shadow: 0 0 0 3px rgba(20, 20, 19, .1); }
	.inputs .dash { padding-bottom: 8px; color: #98a2b3; }
	.months { position: relative; display: flex; gap: 24px; margin-top: 14px; }
	.nav { position: absolute; top: -2px; display: grid; place-items: center; width: 30px; height: 30px; padding: 0; border: 1px solid #eaecf0; border-radius: 8px; background: #fff; color: #344054; cursor: pointer; }
	.nav:hover { background: #f2f4f7; }
	.nav:focus-visible { outline: 2px solid #141413; outline-offset: 1px; }
	.nav.prev { left: 0; }
	.nav.next { right: 0; }
	.month { width: 252px; }
	.month-title { height: 26px; margin-bottom: 8px; color: #182230; font-size: 14px; font-weight: 600; line-height: 26px; text-align: center; }
	.caption { display: flex; justify-content: center; gap: 6px; }
	.caption select {
		height: 28px; padding: 0 4px; border: 1px solid #eaecf0; border-radius: 6px; background: #fff;
		color: #182230; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
	}
	.caption select:hover { border-color: #d0d5dd; }
	.caption select:focus-visible { outline: 2px solid #141413; outline-offset: 1px; }
	.weekdays, .days { display: grid; grid-template-columns: repeat(7, 36px); }
	.weekdays span { height: 28px; color: #98a2b3; font-size: 11px; font-weight: 500; line-height: 28px; text-align: center; text-transform: capitalize; }
	.cell { position: relative; height: 36px; }
	.cell.band::before, .cell.band-start::before, .cell.band-end::before { content: ""; position: absolute; top: 2px; bottom: 2px; background: #f2f4f7; }
	.cell.band::before { left: 0; right: 0; }
	.cell.band-start::before { left: 50%; right: 0; }
	.cell.band-end::before { left: 0; right: 50%; }
	.cell.preview::before { background: #f8f9fb; }
	.day {
		position: relative; display: grid; place-items: center; width: 36px; height: 36px; padding: 0; border: 0; border-radius: 8px;
		background: transparent; color: #182230; font-size: 13px; font-variant-numeric: tabular-nums; cursor: pointer;
	}
	.day:hover:not(:disabled) { background: #eaecf0; }
	.day:focus-visible { outline: 2px solid #141413; outline-offset: -2px; z-index: 1; }
	.day.today::after { content: ""; position: absolute; bottom: 5px; left: 50%; width: 4px; height: 4px; margin-left: -2px; border-radius: 50%; background: #141413; }
	.day.edge { background: #141413; color: #fff; font-weight: 600; }
	.day.edge.today::after { background: #fff; }
	.day.edge.preview { background: #fff; box-shadow: inset 0 0 0 1.5px #141413; color: #141413; }
	.day:disabled { color: #d0d5dd; cursor: default; }
	.footer { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 12px; padding-top: 12px; border-top: 1px solid #eaecf0; }
	.hint { flex: 1; min-width: 120px; color: #667085; font-size: 12px; }
	.btn { height: 32px; padding: 0 14px; border: 1px solid #d0d5dd; border-radius: 8px; background: #fff; color: #344054; font-weight: 500; cursor: pointer; }
	.btn:hover:not(:disabled) { background: #f9fafb; }
	.btn.primary { border-color: #141413; background: #141413; color: #fff; }
	.btn.primary:hover:not(:disabled) { background: #2b2b29; }
	.btn.link { border-color: transparent; background: transparent; color: #667085; }
	.btn:disabled { opacity: .45; cursor: default; }
	.btn:focus-visible { outline: 2px solid #141413; outline-offset: 2px; }
`;

interface DateRangePickerProps {
	portalContainer?: () => HTMLElement;
	from: string;
	to: string;
	onChange: (from: string, to: string) => void;
	label?: string;
	/** Labels UTC ranges and uses the UTC day for Today and relative presets. */
	timeZone?: "UTC";
	/** Defaults to the common periods; pass false to hide the presets column. */
	presets?: DateRangePreset[] | false;
	min?: string;
	max?: string;
	/** Lets the user reset the range to empty, for filters where "no dates" means "any date". */
	allowClear?: boolean;
	placeholder?: string;
	disabled?: boolean;
	style?: CSSProperties;
	/** One date instead of a range: a click picks it, month and year switch from dropdowns. Use DatePickerField. */
	single?: boolean;
	size?: "small" | "medium";
	invalid?: boolean;
	id?: string;
	/** Opens on mount, for inline editors that swap a value for the picker. */
	autoOpen?: boolean;
	/** Called when the popover closes without a new value. */
	onDismiss?: () => void;
}

export const DateRangePicker = ({ from, to, onChange, label, timeZone, presets, min, max, allowClear, placeholder, disabled, style, single, size = "small", invalid, id, autoOpen, onDismiss, portalContainer }: DateRangePickerProps) => {
	useLocale();
	const { t } = useTranslation();
	const locale = t("dashboard.periods.locale");
	const today = timeZone === "UTC" ? toUTCISODate(new Date()) : toISODate(new Date());
	const presetList = presets === false ? [] : presets ?? (single ? [] : getDefaultDateRangePresets(t, today));
	const activePreset = presetList.find(preset => preset.active ?? (!!preset.range && preset.range[0] === from && preset.range[1] === to));
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState({ start: "", end: "" });
	const { start, end } = draft;
	const [hover, setHover] = useState("");
	const [view, setView] = useState(() => monthOf(today));
	const [focused, setFocused] = useState(() => today);
	const [monthCount, setMonthCount] = useState(2);
	const [narrow, setNarrow] = useState(false);
	const [startText, setStartText] = useState("");
	const [endText, setEndText] = useState("");
	const [position, setPosition] = useState<CSSProperties>({ top: 0, left: 0, visibility: "hidden" });
	const triggerRef = useRef<HTMLButtonElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	const focusDay = useRef(false);
	const labelId = useId();

	const inputFormat = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
	const formatInput = (value: string) => value ? inputFormat.format(fromISODate(value)!) : "";
	const ru = locale.startsWith("ru");
	const inputMask = inputFormat.formatToParts(new Date(2026, 0, 31))
		.map(part => part.type === "day" ? (ru ? msg("message") : "dd") : part.type === "month" ? (ru ? msg("message_dd6bb863") : "mm") : part.type === "year" ? (ru ? msg("message_6cc3a6ce") : "yyyy") : part.value).join("");
	const formatRange = (rangeFrom: string, rangeTo: string) => {
		const a = fromISODate(rangeFrom), b = fromISODate(rangeTo);
		if (!a && !b) return "";
		const full = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" });
		if (!a || !b) return full.format((a ?? b)!);
		if (rangeFrom === rangeTo) return full.format(a);
		const short = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" });
		return `${a.getFullYear() === b.getFullYear() ? short.format(a) : full.format(a)} – ${full.format(b)}`;
	};
	const monthTitle = (month: string) => {
		const date = fromISODate(month)!;
		const name = new Intl.DateTimeFormat(locale, { month: "long" }).format(date);
		return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${date.getFullYear()}`;
	};
	const monthNames = Array.from({ length: 12 }, (_, index) => {
		const name = new Intl.DateTimeFormat(locale, { month: "long" }).format(new Date(2026, index, 1));
		return `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
	});
	// Birth dates need decades back, schedules a few years ahead; the shown year is always included.
	const currentYear = Number(today.slice(0, 4)), viewYear = Number(view.slice(0, 4));
	const firstYear = Math.min(min ? Number(min.slice(0, 4)) : currentYear - 100, viewYear);
	const lastYear = Math.max(max ? Number(max.slice(0, 4)) : currentYear + 10, viewYear);
	const years = Array.from({ length: lastYear - firstYear + 1 }, (_, index) => lastYear - index);
	const weekdays = Array.from({ length: 7 }, (_, index) => new Intl.DateTimeFormat(locale, { weekday: "short" }).format(new Date(2024, 0, 1 + index)).replace(".", "").slice(0, 2));
	const isDisabled = (day: string) => (!!min && day < min) || (!!max && day > max);

	const updateLayout = () => {
		const trigger = triggerRef.current, popover = popoverRef.current;
		if (!trigger || !popover) return;
		const viewportWidth = window.innerWidth, viewportHeight = window.innerHeight;
		const isNarrow = viewportWidth < 640;
		setNarrow(isNarrow);
		setMonthCount(!single && viewportWidth >= 720 ? 2 : 1);
		const rect = trigger.getBoundingClientRect();
		const width = isNarrow ? viewportWidth - 16 : popover.offsetWidth;
		const height = popover.offsetHeight;
		let top = rect.bottom + 6;
		if (top + height > viewportHeight - 8) top = rect.top - 6 - height >= 8 ? rect.top - 6 - height : Math.max(8, viewportHeight - height - 8);
		const left = isNarrow ? 8 : Math.min(Math.max(8, rect.left), viewportWidth - width - 8);
		setPosition({ top, left, width: isNarrow ? width : undefined });
	};

	const openPicker = () => {
		const anchor = to || from || today;
		const count = !single && window.innerWidth >= 720 ? 2 : 1;
		setDraft({ start: from, end: to });
		setHover("");
		setMonthCount(count);
		setView(addMonths(monthOf(anchor), -(count - 1)));
		setFocused(anchor);
		setPosition({ top: 0, left: 0, visibility: "hidden" });
		focusDay.current = true;
		setOpen(true);
	};

	const close = (restoreFocus = true, changed = false) => {
		setOpen(false);
		if (restoreFocus) triggerRef.current?.focus();
		if (!changed) onDismiss?.();
	};

	const commit = (nextFrom: string, nextTo: string) => {
		onChange(nextFrom, nextTo);
		close(true, true);
	};

	useEffect(() => {
		if (autoOpen) openPicker();
	}, []);

	useLayoutEffect(() => {
		if (!open) return;
		updateLayout();
		const onViewportChange = () => updateLayout();
		window.addEventListener("resize", onViewportChange);
		window.addEventListener("scroll", onViewportChange, true);
		return () => {
			window.removeEventListener("resize", onViewportChange);
			window.removeEventListener("scroll", onViewportChange, true);
		};
	}, [open, monthCount, narrow, presetList.length]);

	useEffect(() => {
		if (!open) return;
		const onPointerDown = (event: PointerEvent) => {
			const target = event.target as Node;
			if (!popoverRef.current?.contains(target) && !triggerRef.current?.contains(target)) close(false);
		};
		// Escape must not reach document-level listeners, otherwise it also closes the panel the picker sits in.
		const popover = popoverRef.current;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return;
			event.preventDefault();
			event.stopPropagation();
			close();
		};
		document.addEventListener("pointerdown", onPointerDown, true);
		popover?.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("pointerdown", onPointerDown, true);
			popover?.removeEventListener("keydown", onKeyDown);
		};
	}, [open]);

	useEffect(() => setStartText(formatInput(start)), [start, locale]);
	useEffect(() => setEndText(formatInput(end)), [end, locale]);

	useEffect(() => {
		// The popover is hidden until it is positioned, and hidden elements cannot take focus — keep trying until it is shown.
		if (!open || !focusDay.current || position.visibility === "hidden") return;
		const day = popoverRef.current?.querySelector<HTMLButtonElement>(`[data-day="${focused}"]`);
		day?.focus();
		if (day && document.activeElement === day) focusDay.current = false;
	}, [open, focused, view, position]);

	const showMonthOf = (day: string) => {
		if (day < view) setView(monthOf(day));
		else if (day >= addMonths(view, monthCount)) setView(addMonths(monthOf(day), -(monthCount - 1)));
	};

	const select = (day: string) => {
		if (isDisabled(day)) return;
		if (single) return commit(day, day);
		// First click starts a new range; the second closes it, swapping ends when it lands before the start.
		setDraft(current => !current.start || current.end ? { start: day, end: "" }
			: day < current.start ? { start: day, end: current.start } : { start: current.start, end: day });
		setFocused(day);
		setHover("");
	};

	const apply = () => {
		const nextFrom = start || end, nextTo = end || start;
		if (nextFrom || allowClear) commit(nextFrom, nextTo);
		else close();
	};

	const choosePreset = (preset: DateRangePreset) => {
		if (preset.onSelect) {
			preset.onSelect();
			close(true, true);
		} else if (preset.range) commit(preset.range[0], preset.range[1]);
	};

	const parseInput = (text: string) => {
		const parts = text.trim().split(/[./\-\s]+/).filter(Boolean).map(Number);
		if (parts.length !== 3 || parts.some(Number.isNaN)) return "";
		let [day, month, year] = parts;
		if (String(text.trim().split(/[./\-\s]+/)[0]).length === 4) [year, month, day] = parts;
		if (year < 100) year += 2000;
		const value = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
		return fromISODate(value) && !isDisabled(value) ? value : "";
	};

	const commitInput = (which: "start" | "end") => {
		const text = which === "start" ? startText : endText;
		const value = parseInput(text);
		if (!value) {
			// Unparseable input falls back to the current draft instead of clearing it.
			if (which === "start") setStartText(formatInput(start)); else setEndText(formatInput(end));
			return;
		}
		if (single) {
			setDraft({ start: value, end: value });
			setFocused(value);
			showMonthOf(value);
			return;
		}
		setDraft(current => {
			let nextStart = which === "start" ? value : current.start, nextEnd = which === "end" ? value : current.end;
			if (nextStart && nextEnd && nextEnd < nextStart) [nextStart, nextEnd] = [nextEnd, nextStart];
			return { start: nextStart, end: nextEnd };
		});
		setFocused(value);
		showMonthOf(value);
	};

	const onDayKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, day: string) => {
		const moves: Record<string, () => string> = {
			ArrowLeft: () => addDays(day, -1),
			ArrowRight: () => addDays(day, 1),
			ArrowUp: () => addDays(day, -7),
			ArrowDown: () => addDays(day, 7),
			Home: () => weekStart(day),
			End: () => addDays(weekStart(day), 6),
			PageUp: () => shiftMonths(day, event.shiftKey ? -12 : -1),
			PageDown: () => shiftMonths(day, event.shiftKey ? 12 : 1),
		};
		const move = moves[event.key];
		if (!move) return;
		event.preventDefault();
		const next = move();
		focusDay.current = true;
		setFocused(next);
		showMonthOf(next);
		if (start && !end) setHover(next);
	};

	// While the end is being picked, the hovered day previews where the range would end.
	const pickingEnd = !!start && !end;
	const previewEnd = pickingEnd && hover ? hover : "";
	const rangeEnd = end || previewEnd;
	const low = rangeEnd && rangeEnd < start ? rangeEnd : start;
	const high = rangeEnd && rangeEnd < start ? start : rangeEnd;

	const renderMonth = (month: string, slot: number) => {
		const offset = (fromISODate(month)!.getDay() + 6) % 7;
		const first = addDays(month, -offset);
		const days = Array.from({ length: 42 }, (_, index) => addDays(first, index));
		const monthKey = month.slice(0, 7);
		return <div className="month" key={slot}>
			{single ? <div className="month-title caption">
				<select aria-label={t("dateRange.month")} value={Number(month.slice(5, 7)) - 1} onChange={event => setView(toISODate(new Date(Number(month.slice(0, 4)), Number(event.target.value), 1)))}>
					{monthNames.map((name, index) => <option key={name} value={index}>{name}</option>)}
				</select>
				<select aria-label={t("dateRange.year")} value={Number(month.slice(0, 4))} onChange={event => setView(`${event.target.value}${month.slice(4)}`)}>
					{years.map(year => <option key={year} value={year}>{year}</option>)}
				</select>
			</div> : <div className="month-title" aria-live="polite">{monthTitle(month)}</div>}
			<div className="weekdays" aria-hidden="true">{weekdays.map(day => <span key={day}>{day}</span>)}</div>
			<div className="days" role="grid" aria-label={monthTitle(month)}>
				{days.map(day => {
					if (day.slice(0, 7) !== monthKey) return <div className="cell" key={day} />;
					const isEdge = day === low || day === high;
					const hasRange = !!low && !!high && low !== high;
					const cellClass = !hasRange ? "" : day === low ? "band-start" : day === high ? "band-end" : day > low && day < high ? "band" : "";
					const isPreview = !!previewEnd && hasRange;
					return <div className={`cell ${cellClass}${isPreview ? " preview" : ""}`} key={day} role="gridcell" aria-selected={isEdge || (hasRange && day > low && day < high)}>
						<button
							type="button"
							data-day={day}
							className={`day${isEdge ? " edge" : ""}${day === today ? " today" : ""}${isEdge && isPreview && day === previewEnd ? " preview" : ""}`}
							tabIndex={day === focused ? 0 : -1}
							disabled={isDisabled(day)}
							aria-label={new Intl.DateTimeFormat(locale, { dateStyle: "full" }).format(fromISODate(day)!)}
							aria-current={day === today ? "date" : undefined}
							onClick={() => select(day)}
							onMouseEnter={() => pickingEnd && setHover(day)}
							onFocus={() => { setFocused(day); if (pickingEnd) setHover(day); }}
							onKeyDown={event => onDayKeyDown(event, day)}>
							{Number(day.slice(8))}
						</button>
					</div>;
				})}
			</div>
		</div>;
	};

	const hint = start && end
		? `${formatRange(start, end)} · ${t("dateRange.days", { count: dayCount(start, end) })}`
		: start ? t("dateRange.pickEnd") : t("dateRange.pickStart");
	const valueText = formatRange(from, to);

	const fieldName = label ?? t(single ? "dateRange.date" : "dashboard.periods.dateRange");
	const clearable = allowClear && !!(from || to) && !disabled;

	return <Field style={style} className={size === "medium" ? "medium" : undefined}>
		{label && <span id={labelId}><TypoCaption block>{label}:</TypoCaption></span>}
		<div className="control">
			<button
				ref={triggerRef}
				id={id}
				type="button"
				className={`trigger${clearable ? " clearable" : ""}${invalid ? " invalid" : ""}`}
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-invalid={invalid || undefined}
				aria-labelledby={label ? labelId : undefined}
				aria-label={label ? undefined : fieldName}
				disabled={disabled}
				onKeyDown={event => { if (event.key === "Escape" && open) { event.stopPropagation(); close(); } }}
				onClick={() => open ? close() : openPicker()}>
				<CalendarDays size={16} aria-hidden="true" />
				<span className={`value${valueText ? "" : " placeholder"}`}>{valueText || placeholder || t(single ? "dateRange.selectDate" : "dateRange.any")}</span>
				{timeZone && valueText && <span className="timezone">{timeZone}</span>}
				{activePreset && <span className="preset">{activePreset.label}</span>}
				<ChevronDown className="chevron" size={16} aria-hidden="true" />
			</button>
			{clearable && <button type="button" className="clear" aria-label={t("dateRange.clear")} onClick={() => onChange("", "")}>
				<X size={14} aria-hidden="true" />
			</button>}
		</div>
		{open && createPortal(<Popover ref={popoverRef} className={narrow ? "narrow" : undefined} style={position} role="dialog" aria-modal="false" aria-label={fieldName}
			onKeyDown={event => { if (!single && event.key === "Enter" && !(event.target as HTMLElement).matches("button, input, select")) apply(); }}>
			{!!presetList.length && <div className="presets" role="group" aria-label={t("dateRange.presets")}>
				<div className="presets-title">{t("dateRange.presets")}</div>
				{presetList.map(preset => <button type="button" key={preset.label} className="preset" aria-pressed={preset === activePreset} onClick={() => choosePreset(preset)}>{preset.label}</button>)}
			</div>}
			<div className="main">
				{single ? <div className="inputs">
					<label>{t("dateRange.date")}
						<input value={startText} inputMode="numeric" placeholder={inputMask}
							onChange={event => setStartText(event.target.value)} onBlur={() => commitInput("start")}
							onKeyDown={event => {
								if (event.key !== "Enter") return;
								event.preventDefault();
								const value = parseInput(startText);
								if (value) commit(value, value); else commitInput("start");
							}} />
					</label>
				</div> : <div className="inputs">
					<label>{t("dateRange.start")}
						<input value={startText} inputMode="numeric" placeholder={inputMask} className={!start ? "pending" : undefined}
							onChange={event => setStartText(event.target.value)} onBlur={() => commitInput("start")}
							onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); commitInput("start"); } }} />
					</label>
					<span className="dash" aria-hidden="true">–</span>
					<label>{t("dateRange.end")}
						<input value={endText} inputMode="numeric" placeholder={inputMask} className={pickingEnd ? "pending" : undefined}
							onChange={event => setEndText(event.target.value)} onBlur={() => commitInput("end")}
							onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); commitInput("end"); } }} />
					</label>
				</div>}
				<div className="months" onMouseLeave={() => setHover("")}>
					<button type="button" className="nav prev" aria-label={t("dateRange.previousMonth")} onClick={() => setView(addMonths(view, -1))}><ChevronLeft size={16} aria-hidden="true" /></button>
					<button type="button" className="nav next" aria-label={t("dateRange.nextMonth")} onClick={() => setView(addMonths(view, 1))}><ChevronRight size={16} aria-hidden="true" /></button>
					{Array.from({ length: monthCount }, (_, index) => renderMonth(addMonths(view, index), index))}
				</div>
				{single ? <div className="footer">
					<span className="hint">
						{!isDisabled(today) && <button type="button" className="btn" onClick={() => commit(today, today)}>{t("dateRange.today")}</button>}
					</span>
					{allowClear && from && <button type="button" className="btn link" onClick={() => commit("", "")}>{t("dateRange.clear")}</button>}
					<button type="button" className="btn" onClick={() => close()}>{t("dateRange.cancel")}</button>
				</div> : <div className="footer">
					<span className="hint" aria-live="polite">{hint}</span>
					{allowClear && (start || end) && <button type="button" className="btn link" onClick={() => commit("", "")}>{t("dateRange.clear")}</button>}
					<button type="button" className="btn" onClick={() => close()}>{t("dateRange.cancel")}</button>
					<button type="button" className="btn primary" disabled={!start && !end && !allowClear} onClick={apply}>{t("dateRange.apply")}</button>
				</div>}
			</div>
		</Popover>, portalContainer?.() ?? document.body)}
	</Field>;
};
